/** Read CSRF token from the document meta tag Rails renders. */
export function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

/** Keep `<meta name="csrf-token">` in sync after login / session rotation / Inertia visits. */
export function setCsrfToken(token: string): void {
  const value = token.trim()
  if (!value || typeof document === "undefined") return

  let meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
  if (!meta) {
    meta = document.createElement("meta")
    meta.name = "csrf-token"
    document.head.appendChild(meta)
  }
  meta.content = value
}

let refreshInFlight: Promise<string> | null = null

/**
 * Re-fetch the current HTML document to pick up a fresh authenticity token.
 * Needed on long-lived pages (e.g. research Show) when the session rotated
 * after the initial meta tag was rendered.
 */
export async function refreshCsrfToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${window.location.pathname}${window.location.search}`, {
        credentials: "include",
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
      })
      const html = await response.text()
      const match =
        html.match(/name=["']csrf-token["']\s+content=["']([^"']+)["']/i) ||
        html.match(/content=["']([^"']+)["']\s+name=["']csrf-token["']/i)
      const token = match?.[1]?.trim() ?? ""
      if (token) setCsrfToken(token)
      return token || getCsrfToken()
    } catch {
      return getCsrfToken()
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

type CsrfFetchInit = RequestInit & { retryOnCsrfFailure?: boolean }

/** fetch with CSRF header; on 422 authenticity failure, refresh token once and retry. */
export async function fetchWithCsrf(input: RequestInfo | URL, init: CsrfFetchInit = {}): Promise<Response> {
  const { retryOnCsrfFailure = true, headers, ...rest } = init
  const headerBag = new Headers(headers)
  if (!headerBag.has("X-CSRF-Token")) {
    headerBag.set("X-CSRF-Token", getCsrfToken())
  }

  const response = await fetch(input, {
    credentials: "include",
    ...rest,
    headers: headerBag,
  })

  if (response.status !== 422 || !retryOnCsrfFailure) return response

  const token = await refreshCsrfToken()
  if (!token) return response

  const retryHeaders = new Headers(headers)
  retryHeaders.set("X-CSRF-Token", token)
  return fetch(input, {
    credentials: "include",
    ...rest,
    headers: retryHeaders,
  })
}
